import { Tag } from 'antd';
import { isNil, startCase as _startCase } from 'lodash';
import { useTheme } from 'styled-components';

import {
  type AtlasGqlInspectionDeliveryStatus,
  type AtlasGqlTaskStatus,
  AtlasGqlInspectionStatus,
} from 'types/atlas-graphql';
import { type HorizonTheme } from 'utils/color/horizonTheme';

export function getSeverityColor(
  theme: HorizonTheme,
  severity?: number | null,
  maxValue: number = 5
) {
  if (isNil(severity)) {
    return null;
  }
  // Cat 5 red
  if (severity >= maxValue) {
    return theme.red;
  }
  // Cat 4 orange
  if (severity >= maxValue - 1) {
    return theme.orange;
  }
  // Cat 3 yellow
  if (severity >= maxValue - 2) {
    return theme.yellow;
  }
  // Cat 2 light green
  if (severity >= maxValue - 3) {
    return theme.green;
  }
  // Cat 1 dark green
  return theme.darkGreen;
}

export const getStatusColor = (status: string, theme: HorizonTheme) => {
  switch (status) {
    case 'IN_PROGRESS':
    case AtlasGqlInspectionStatus.AnalysisPending:
    case AtlasGqlInspectionStatus.AnalysisInProgress:
      return theme.teal;
    case 'PENDING':
    case 'DRAFT':
    case AtlasGqlInspectionStatus.DataCapturePending:
    case AtlasGqlInspectionStatus.DataCaptureInProgress:
      return theme.defaultStatusColor;
    case 'COMPLETE':
    case 'COMPLETED':
    case 'RELEASED':
    case AtlasGqlInspectionStatus.AnalysisComplete:
      return theme.green;
    case 'FAILED':
    case 'BLOCKED':
      return theme.red;
    case 'PLANNING':
      return theme.purple;
    case 'SCHEDULED':
      return theme.lightBlue;
    case 'DEFERRED':
      return theme.secondaryBackgroundColor;
    default:
      return undefined;
  }
};

export const StatusTag: React.FC<{
  status: AtlasGqlTaskStatus | AtlasGqlInspectionStatus | AtlasGqlInspectionDeliveryStatus;
  startCase?: boolean;
}> = ({ status, startCase = true }) => {
  const theme = useTheme();

  return (
    <Tag color={getStatusColor(status, theme)}>
      {startCase ? _startCase(status.toLowerCase()) : status}
    </Tag>
  );
};

export type AssetColorStatus =
  | 'Active'
  | 'Curtailed'
  | 'Decommissioned'
  | 'Offline'
  | 'Online'
  | 'Recycled'
  | 'Storage';

export function getAssetColorStatus(status: AssetColorStatus, theme: HorizonTheme) {
  switch (status) {
    case 'Offline':
    case 'Recycled':
      return theme.red;
    case 'Online':
    case 'Active':
      return theme.teal;
    case 'Curtailed':
    case 'Storage':
      return theme.severityHighColor;
    case 'Decommissioned':
      return theme.secondaryBackgroundColor;
    default:
      return theme.secondaryBackgroundColor;
  }
}

export const getOpSeverityColor = (
  theme: HorizonTheme,
  opSeverity?: number
): string | undefined => {
  if (isNil(opSeverity) || !Number.isFinite(opSeverity)) {
    return undefined;
  }

  // 1.0 - 3.9: use default text color
  if (opSeverity <= 3.9) {
    return undefined;
  }

  if (opSeverity <= 4.4) {
    return theme.opSeverity.pureOrange;
  }

  if (opSeverity <= 4.8) {
    return theme.opSeverity.deepOrange;
  }

  if (opSeverity <= 4.9) {
    return theme.opSeverity.darkBurntOrange;
  }

  if (opSeverity <= 5.4) {
    return theme.opSeverity.redAnchor;
  }

  if (opSeverity <= 5.8) {
    return theme.opSeverity.crimson;
  }

  return theme.opSeverity.bloodRed;
};

// Green under 5%, orange through 20%, red above it.
export const PROPAGATION_PROBABILITY_ELEVATED = 5;
export const PROPAGATION_PROBABILITY_HIGH = 20;

// Colors a probability by magnitude, unlike getSeverityColor, which maps a
// severity rating.
export const getPropagationProbabilityColor = (
  theme: HorizonTheme,
  percent?: number | null
): string => {
  if (isNil(percent) || !Number.isFinite(percent)) {
    return theme.secondaryTextColor;
  }

  if (percent > PROPAGATION_PROBABILITY_HIGH) {
    return theme.red;
  }

  if (percent >= PROPAGATION_PROBABILITY_ELEVATED) {
    return theme.orange;
  }

  return theme.green;
};
